import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  FileText,
  BarChart3,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Star,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Shield,
  CheckCircle,
  Brain,
  Users,
  ExternalLink,
  Pill,
  Hospital,
  Briefcase,
  Timer,
  Award,
  Keyboard,
  Home,
  Play,
  ClipboardCheck,
  Sparkles,
  GripVertical,
  Plus,
  X,
  AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { LoadingState, ErrorState, Breadcrumb } from '../../components/ui';
import { useAuthStore } from '../../stores/auth-store';

type AnalysisTab = 'summary' | 'assessment' | 'expectations' | 'documents';

interface Execution {
  id: string;
  blindCode: string;
  status: string;
  indication?: {
    suitability: string;
    confidence: number;
    summary: string;
    issues: Array<{
      severity: string;
      description: string;
      recommendation?: string;
    }>;
    patientExpectations?: {
      summary: string;
      concerns: string[];
      goals: string[];
    };
    documentFindings?: {
      hasDocuments: boolean;
      hasInconsistencies: boolean;
      findings: Array<{
        documentName: string;
        observations: string[];
        clinicalSignificance?: string;
      }>;
      inconsistencies?: Array<{
        description: string;
        severity: string;
        documentSource: string;
        questionnaireSource?: string;
      }>;
      verificationStatus?: string;
    };
    // ICD-10 codes for BetterDoc integration
    icd10Codes?: {
      leadingCondition?: string;
      leadingConditionName?: string;
      otherConditions?: Array<{ code: string; name: string }> | string[];
    };
  };
  criteria?: {
    criteria: Array<{
      id: string;
      field: string;
      value: string | string[];
      priority: string;
      reasoning: string;
      // Additional fields for UI display (matching Andy Pro)
      name?: string;
      description?: string;
      importance?: string;
      rationale?: string; // Detailed clinical rationale for this criterion
    }>;
    priorityExplanation?: string;
    additionalNotes?: string;
  };
  latencyCategory?: string;
}

type RunType = 'MODEL_COMPARISON' | 'MODEL_STABILITY';

interface TestRun {
  id: string;
  name: string;
  status: string;
  isRevealed: boolean;
  runType?: RunType;
  testCase: {
    id: string;
    name: string;
    code: string;
    condition: string;
    patientInfo: {
      age: number;
      gender: string;
      chiefComplaint: string;
    };
    answers: Array<{ question: string; answer: string }>;
    documents?: Array<{ id: string; originalName: string; mimeType: string }>;
  };
  executions: Execution[];
}

const VERDICTS = [
  { value: 'PASS', label: 'Pass', icon: ThumbsUp, color: 'text-green-600 bg-green-100 border-green-200', shortcut: '1', score: 100 },
  { value: 'PASS_WITH_RESERVATIONS', label: 'Pass with Reservations', icon: Check, color: 'text-blue-600 bg-blue-100 border-blue-200', shortcut: '2', score: 85 },
  { value: 'BORDERLINE', label: 'Borderline', icon: Minus, color: 'text-amber-600 bg-amber-100 border-amber-200', shortcut: '3', score: 65 },
  { value: 'FAIL_MINOR', label: 'Fail (Minor)', icon: AlertCircle, color: 'text-orange-600 bg-orange-100 border-orange-200', shortcut: '4', score: 45 },
  { value: 'FAIL_MAJOR', label: 'Fail (Major)', icon: ThumbsDown, color: 'text-red-600 bg-red-100 border-red-200', shortcut: '5', score: 25 },
  { value: 'FAIL_CRITICAL', label: 'Fail (Critical)', icon: Shield, color: 'text-red-700 bg-red-200 border-red-300', shortcut: '6', score: 0 },
];

// Helper to get verdict score for auto-ranking
const getVerdictScore = (verdict?: string): number => {
  const found = VERDICTS.find((v) => v.value === verdict);
  return found?.score ?? -1;
};

// Helper to get verdict config
const getVerdictConfig = (verdict?: string) => {
  return VERDICTS.find((v) => v.value === verdict);
};

// Progress indicator component
function ProgressIndicator({
  total,
  verdicts,
}: {
  current: number;
  total: number;
  verdicts: Record<string, string>;
  rankings: Record<string, number>;
}) {
  const verdictsComplete = Object.keys(verdicts).length;
  const progress = total > 0 ? (verdictsComplete / total) * 100 : 0;
  const isComplete = verdictsComplete === total;

  return (
    <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 -mx-4 px-4 py-3 mb-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Evaluation Progress</span>
            <div className="flex items-center gap-1">
              {Array.from({ length: total }).map((_, i) => {
                const isVerdictSet = i < verdictsComplete;
                return (
                  <div
                    key={i}
                    className={clsx(
                      'w-6 h-2 rounded-full transition-colors',
                      isVerdictSet ? 'bg-green-500' : 'bg-gray-200'
                    )}
                  />
                );
              })}
            </div>
          </div>
          <span className="text-sm text-gray-500">
            {verdictsComplete}/{total} verdicts
            {isComplete && (
              <span className="ml-2 text-green-600 font-medium">
                • Rankings auto-generated ✓
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-300',
                progress >= 100 ? 'bg-green-500' : progress >= 50 ? 'bg-amber-500' : 'bg-gray-400'
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-sm font-medium text-gray-600">{Math.round(progress)}%</span>
        </div>
      </div>
    </div>
  );
}

// Keyboard shortcuts help panel
function KeyboardShortcutsHelp({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-gray-400" />
            Keyboard Shortcuts
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Verdicts</div>
          <div className="grid grid-cols-2 gap-2">
            {VERDICTS.map((v) => (
              <div key={v.value} className="flex items-center gap-2 text-sm">
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">{v.shortcut}</kbd>
                <span className="text-gray-600">{v.label}</span>
              </div>
            ))}
          </div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-4">Navigation</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 text-sm">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">↑/↓</kbd>
              <span className="text-gray-600">Navigate cards</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">E</kbd>
              <span className="text-gray-600">Expand/collapse</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">?</kbd>
              <span className="text-gray-600">Show shortcuts</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Enter</kbd>
              <span className="text-gray-600">Submit (when ready)</span>
            </div>
          </div>
        </div>
        <p className="mt-4 text-xs text-gray-400 text-center">
          Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Esc</kbd> to close
        </p>
      </div>
    </div>
  );
}

// Suitability badge styling is now inline in the Summary tab to match Andy Pro workflow exactly

// Auto-Ranking Section Component
interface RankedExecution {
  blindCode: string;
  verdict?: string;
  score: number;
}

function AutoRankingSection({
  executions,
  verdicts,
  rankings,
  onRankingsChange,
  isReadOnly = false,
}: {
  executions: Execution[];
  verdicts: Record<string, string>;
  rankings: Record<string, number>;
  onRankingsChange: (newRankings: Record<string, number>) => void;
  isReadOnly?: boolean;
}) {
  const allVerdictsComplete = executions.every((e) => verdicts[e.blindCode]);

  // Calculate auto-ranking based on verdict scores
  const rankedExecutions: RankedExecution[] = executions
    .map((e) => ({
      blindCode: e.blindCode,
      verdict: verdicts[e.blindCode],
      score: getVerdictScore(verdicts[e.blindCode]),
    }))
    .sort((a, b) => b.score - a.score);

  // Get current ranking order (from rankings state or use auto-order)
  const getCurrentOrder = (): string[] => {
    if (Object.keys(rankings).length === executions.length) {
      // Use existing rankings
      return [...executions]
        .sort((a, b) => (rankings[a.blindCode] || 999) - (rankings[b.blindCode] || 999))
        .map((e) => e.blindCode);
    }
    // Use auto-ranking
    return rankedExecutions.map((e) => e.blindCode);
  };

  const currentOrder = getCurrentOrder();
  const hasCustomRankings = Object.keys(rankings).length > 0;

  // Apply auto-ranking to state
  const handleApplyAutoRanking = () => {
    const newRankings: Record<string, number> = {};
    rankedExecutions.forEach((e, idx) => {
      newRankings[e.blindCode] = idx + 1;
    });
    onRankingsChange(newRankings);
  };

  // Move execution up in ranking
  const handleMoveUp = (blindCode: string) => {
    const currentIdx = currentOrder.indexOf(blindCode);
    if (currentIdx <= 0) return;

    const newOrder = [...currentOrder];
    [newOrder[currentIdx - 1], newOrder[currentIdx]] = [newOrder[currentIdx], newOrder[currentIdx - 1]];

    const newRankings: Record<string, number> = {};
    newOrder.forEach((code, idx) => {
      newRankings[code] = idx + 1;
    });
    onRankingsChange(newRankings);
  };

  // Move execution down in ranking
  const handleMoveDown = (blindCode: string) => {
    const currentIdx = currentOrder.indexOf(blindCode);
    if (currentIdx >= currentOrder.length - 1) return;

    const newOrder = [...currentOrder];
    [newOrder[currentIdx], newOrder[currentIdx + 1]] = [newOrder[currentIdx + 1], newOrder[currentIdx]];

    const newRankings: Record<string, number> = {};
    newOrder.forEach((code, idx) => {
      newRankings[code] = idx + 1;
    });
    onRankingsChange(newRankings);
  };

  if (!allVerdictsComplete) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <div className="flex items-center gap-3 text-gray-500">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200">
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-700">Final Ranking</h3>
            <p className="text-sm">Complete all verdicts above to see the auto-generated ranking</p>
          </div>
        </div>
        {/* Progress indicator */}
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all"
              style={{ width: `${(Object.keys(verdicts).length / executions.length) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">
            {Object.keys(verdicts).length}/{executions.length} verdicts
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <BarChart3 className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Final Ranking</h3>
            <p className="text-xs text-gray-500">
              {isReadOnly
                ? 'Recorded ranking'
                : hasCustomRankings
                ? 'Custom order • Adjust with arrows if needed'
                : 'Auto-generated from verdicts • Adjust with arrows if needed'}
            </p>
          </div>
        </div>
        {!isReadOnly && (
          <button
            onClick={handleApplyAutoRanking}
            className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            Reset to Auto
          </button>
        )}
      </div>

      {/* Ranking List */}
      <div className="p-4">
        <div className="space-y-2">
          {currentOrder.map((blindCode, index) => {
            const verdict = verdicts[blindCode];
            const verdictConfig = getVerdictConfig(verdict);
            const score = getVerdictScore(verdict);
            const isFirst = index === 0;
            const isLast = index === currentOrder.length - 1;

            return (
              <div
                key={blindCode}
                className={clsx(
                  'flex items-center gap-3 p-3 rounded-lg border transition-all',
                  index === 0 && 'bg-amber-50 border-amber-200',
                  index === 1 && 'bg-gray-50 border-gray-200',
                  index === 2 && 'bg-orange-50/50 border-orange-200/50',
                  index > 2 && 'bg-white border-gray-200'
                )}
              >
                {/* Rank Badge */}
                <div
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold shrink-0',
                    index === 0 && 'bg-amber-500 text-white',
                    index === 1 && 'bg-gray-400 text-white',
                    index === 2 && 'bg-orange-400 text-white',
                    index > 2 && 'bg-gray-200 text-gray-600'
                  )}
                >
                  {index + 1}
                </div>

                {/* Grip indicator */}
                <GripVertical className="h-4 w-4 text-gray-300" />

                {/* Blind Code */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-gray-900">{blindCode}</span>
                    {verdictConfig && (
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                          verdictConfig.color
                        )}
                      >
                        <verdictConfig.icon className="h-3 w-3" />
                        {verdictConfig.label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Score: {score}</p>
                </div>

                {/* Move Buttons */}
                {!isReadOnly && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleMoveUp(blindCode)}
                      disabled={isFirst}
                      className={clsx(
                        'p-1.5 rounded-md transition-colors',
                        isFirst
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      )}
                      title="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(blindCode)}
                      disabled={isLast}
                      className={clsx(
                        'p-1.5 rounded-md transition-colors',
                        isLast
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      )}
                      title="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
            {isReadOnly
              ? 'Rankings were calculated from verdicts.'
              : 'Rankings are auto-calculated from verdicts. Use arrows to adjust if clinical judgment differs.'}
          </p>
        </div>
      </div>
    </div>
  );
}


function ExecutionCard({
  execution,
  verdict,
  note,
  onVerdictChange,
  onNoteChange,
  hasDocuments,
  disabledCriteria,
  onToggleCriterion,
  manualCriteria,
  onOpenAddCriteriaModal,
  onOpenDeleteCriteriaModal,
  isReadOnly = false,
  hideEvaluationControls = false,
}: {
  execution: Execution;
  verdict?: string;
  note?: string;
  onVerdictChange: (verdict: string) => void;
  onNoteChange: (note: string) => void;
  hasDocuments: boolean;
  disabledCriteria: Set<string>;
  onToggleCriterion: (criteriaKey: string) => void;
  manualCriteria: Array<{ id: string; name: string; description: string; priority: string }>;
  onOpenAddCriteriaModal: (priority: string) => void;
  onOpenDeleteCriteriaModal: (criteriaId: string, name: string, priority: string) => void;
  isReadOnly?: boolean;
  hideEvaluationControls?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<AnalysisTab>('summary');
  const [expandedRationales, setExpandedRationales] = useState<Set<string>>(new Set());

  const toggleRationale = (criteriaKey: string) => {
    setExpandedRationales(prev => {
      const next = new Set(prev);
      if (next.has(criteriaKey)) {
        next.delete(criteriaKey);
      } else {
        next.add(criteriaKey);
      }
      return next;
    });
  };
  // Note: hasDocumentFindings removed - Documents tab now only shows based on test case having documents

  if (!execution.indication || !execution.criteria) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
            <EyeOff className="h-5 w-5 text-gray-400" />
          </div>
          <div>
            <h3 className="font-mono font-semibold text-gray-900">{execution.blindCode}</h3>
            <p className="text-sm text-gray-500">Execution incomplete or failed</p>
          </div>
        </div>
      </div>
    );
  }

  // Group criteria by priority for display (include manual criteria)
  const allCriteria = [
    ...(execution.criteria.criteria || []),
    ...manualCriteria.map((mc) => ({
      id: mc.id,
      field: mc.name,
      value: '' as string | string[],
      priority: mc.priority,
      reasoning: '',
      name: mc.name,
      description: mc.description,
      rationale: '',
      _isManual: true,
    })),
  ];
  const groupedCriteria = allCriteria.reduce<Record<string, typeof allCriteria>>((acc, c) => {
    const priority = (c.priority || 'low').toLowerCase();
    if (!acc[priority]) acc[priority] = [];
    acc[priority].push(c);
    return acc;
  }, {});

  const priorityOrder = ['critical', 'high', 'medium', 'low'];
  const priorityConfig = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', label: 'Critical', addBtn: 'bg-red-100 text-red-700 hover:bg-red-200' },
    high: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', label: 'High Priority', addBtn: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
    medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', label: 'Medium Priority', addBtn: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
    low: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-600', label: 'Low Priority', addBtn: 'bg-gray-200 text-gray-700 hover:bg-gray-300' },
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <span className="font-mono text-lg font-bold text-amber-700">
              {execution.blindCode.slice(0, 1)}
            </span>
          </div>
          <div>
            <h3 className="font-mono font-semibold text-gray-900">{execution.blindCode}</h3>
            <p className="text-xs text-gray-500">
              Completed • Ready for evaluation
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="rounded p-1 hover:bg-gray-200"
        >
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </div>

      {expanded && (
        <>
          {/* Indication Analysis - Tab-based layout like Andy Pro */}
          <div className="border-b border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
              <h4 className="text-sm font-medium text-gray-900">Case Analysis Results</h4>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                Complete
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 mb-4 p-1 bg-gray-100 rounded-lg">
              {[
                { id: 'summary' as const, label: 'Summary', icon: CheckCircle },
                { id: 'assessment' as const, label: 'Medical Assessment', icon: Brain },
                { id: 'expectations' as const, label: 'Patient Expectations', icon: Users },
                // Only show Documents tab if test case actually has documents attached
                // Don't rely on model output (hasDocumentFindings) as it can be inconsistent
                ...(hasDocuments
                  ? [{ id: 'documents' as const, label: 'Documents', icon: FileText }]
                  : []),
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveAnalysisTab(tab.id)}
                  className={clsx(
                    'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all',
                    activeAnalysisTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  )}
                >
                  <tab.icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="space-y-4">
              {/* Summary Tab - Matching Andy Pro workflow layout */}
              {activeAnalysisTab === 'summary' && (
                <div className="p-4 rounded-lg border border-gray-200 bg-white">
                  {/* Suitability Verdict - Andy Pro style */}
                  <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className={clsx(
                        'p-2 rounded-lg',
                        execution.indication.suitability === 'SUITABLE'
                          ? 'bg-green-100'
                          : execution.indication.suitability === 'UNSUITABLE'
                          ? 'bg-red-100'
                          : 'bg-amber-100'
                      )}>
                        {execution.indication.suitability === 'SUITABLE' ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : execution.indication.suitability === 'UNSUITABLE' ? (
                          <AlertCircle className="h-5 w-5 text-red-600" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">Suitability:</span>
                          <span className={clsx(
                            'px-2 py-0.5 text-xs font-bold rounded uppercase',
                            execution.indication.suitability === 'SUITABLE' && 'bg-green-100 text-green-700',
                            execution.indication.suitability === 'UNSUITABLE' && 'bg-red-100 text-red-700',
                            execution.indication.suitability === 'REQUIRES_REVIEW' && 'bg-amber-100 text-amber-700'
                          )}>
                            {execution.indication.suitability === 'SUITABLE' ? 'SUITABLE' : execution.indication.suitability === 'UNSUITABLE' ? 'NOT SUITABLE' : 'REQUIRES REVIEW'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {execution.indication.summary}
                        </p>
                      </div>
                    </div>
                    {/* Confidence Score */}
                    <div className="text-right">
                      <div className="text-xs text-gray-500 mb-1">Confidence</div>
                      <div className={clsx(
                        'text-lg font-bold',
                        execution.indication.confidence >= 75 && 'text-green-600',
                        execution.indication.confidence >= 50 && execution.indication.confidence < 75 && 'text-yellow-600',
                        execution.indication.confidence < 50 && 'text-red-600'
                      )}>
                        {execution.indication.confidence}%
                      </div>
                    </div>
                  </div>

                  {/* Key Points - Andy Pro style with numbered badges */}
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Key Points</h4>
                    <div className="space-y-2">
                      {(() => {
                        let pointNumber = 0;
                        const points: React.ReactNode[] = [];

                        // Add stated indication as first point
                        if (execution.indication.summary) {
                          pointNumber++;
                          const summary = execution.indication.summary;
                          const firstSentence = summary.includes('.') ? summary.split('.')[0] + '.' : summary;
                          points.push(
                            <div key="indication" className="flex items-start gap-2 text-xs">
                              <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold shrink-0">
                                {pointNumber}
                              </span>
                              <span className="text-gray-700">
                                <strong>Indication:</strong> {firstSentence.length > 120 ? firstSentence.substring(0, 120) + '...' : firstSentence}
                              </span>
                            </div>
                          );
                        }

                        // Add critical/high severity issues
                        (execution.indication.issues || [])
                          .filter(i => i.severity?.toLowerCase() === 'critical' || i.severity?.toLowerCase() === 'high')
                          .slice(0, 2)
                          .forEach((issue, i) => {
                            pointNumber++;
                            points.push(
                              <div key={`issue-${i}`} className="flex items-start gap-2 text-xs">
                                <span className={clsx(
                                  'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                                  issue.severity?.toLowerCase() === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                )}>
                                  {pointNumber}
                                </span>
                                <span className="text-gray-700">{issue.description}</span>
                              </div>
                            );
                          });

                        // Add provider traits count if available
                        if (execution.indication.patientExpectations?.concerns && execution.indication.patientExpectations.concerns.length > 0) {
                          pointNumber++;
                          points.push(
                            <div key="traits" className="flex items-start gap-2 text-xs">
                              <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold shrink-0">
                                {pointNumber}
                              </span>
                              <span className="text-gray-700">
                                <strong>{execution.indication.patientExpectations.concerns.length} provider traits identified</strong> based on case analysis
                              </span>
                            </div>
                          );
                        }

                        return points.length > 0 ? points : (
                          <p className="text-xs text-gray-500 italic">No key points extracted</p>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Matching Considerations (collapsible) - Andy Pro style */}
                  {execution.indication.issues && execution.indication.issues.length > 0 && (
                    <details className="mt-4 pt-4 border-t border-gray-100 group" open>
                      <summary className="flex items-center justify-between cursor-pointer list-none">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          Matching Considerations ({execution.indication.issues.length})
                        </h4>
                        <ChevronDown className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                        {execution.indication.issues.map((issue, idx) => {
                          // Map severity to Andy Pro display (critical/warning/info)
                          const severityLower = issue.severity?.toLowerCase() || 'medium';
                          const severityDisplay = severityLower === 'high' ? 'WARNING' : severityLower === 'low' ? 'INFO' : severityLower.toUpperCase();
                          const bgColor = severityLower === 'critical' ? 'bg-red-50 border-l-red-500' :
                                         (severityLower === 'high' || severityLower === 'medium') ? 'bg-yellow-50 border-l-yellow-500' :
                                         'bg-blue-50 border-l-blue-500';
                          const badgeColor = severityLower === 'critical' ? 'bg-red-100 text-red-700' :
                                            (severityLower === 'high' || severityLower === 'medium') ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-blue-100 text-blue-700';

                          return (
                            <div
                              key={idx}
                              className={clsx('p-2.5 rounded-md text-xs border-l-3', bgColor)}
                            >
                              <div className="flex items-start gap-2">
                                <span className={clsx('px-1.5 py-0.5 text-[9px] font-bold uppercase rounded shrink-0', badgeColor)}>
                                  {severityDisplay}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-800">{issue.description}</div>
                                  {issue.recommendation && (
                                    <div className="text-gray-600 mt-0.5">{issue.recommendation}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Medical Assessment Tab - Matching Andy Pro layout */}
              {activeAnalysisTab === 'assessment' && (
                <div className="p-4 rounded-lg border border-gray-200 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-md bg-blue-100">
                        <Brain className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Medical Assessment</span>
                    </div>
                    <span className={clsx(
                      'px-2 py-0.5 text-xs font-medium rounded',
                      execution.indication.confidence >= 75 && 'bg-green-100 text-green-700',
                      execution.indication.confidence >= 50 && execution.indication.confidence < 75 && 'bg-yellow-100 text-yellow-700',
                      execution.indication.confidence < 50 && 'bg-red-100 text-red-700'
                    )}>
                      {execution.indication.confidence >= 75 ? 'high' : execution.indication.confidence >= 50 ? 'medium' : 'low'} confidence
                    </span>
                  </div>

                  {/* Stated Indication - Andy Pro style */}
                  <div className="mb-3">
                    <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Stated Indication</span>
                    <p className="text-sm font-medium text-gray-800 mt-1">
                      {execution.indication.summary?.includes('.') ? execution.indication.summary.split('.')[0] + '.' : execution.indication.summary}
                    </p>
                  </div>

                  {/* ICD-10 Codes (for BetterDoc) */}
                  {execution.indication.icd10Codes && (
                    execution.indication.icd10Codes.leadingCondition ||
                    (Array.isArray(execution.indication.icd10Codes.otherConditions) && execution.indication.icd10Codes.otherConditions.length > 0)
                  ) && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <span className="text-[10px] font-medium text-blue-600 uppercase tracking-wide flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        ICD-10 Codes (for BetterDoc)
                      </span>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {execution.indication.icd10Codes.leadingCondition && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                            <span className="text-[9px] text-blue-600 font-medium bg-blue-200 px-1 rounded">PRIMARY</span>
                            {execution.indication.icd10Codes.leadingCondition}
                            {execution.indication.icd10Codes.leadingConditionName && (
                              <span className="text-blue-600 font-normal">({execution.indication.icd10Codes.leadingConditionName})</span>
                            )}
                          </span>
                        )}
                        {Array.isArray(execution.indication.icd10Codes.otherConditions) &&
                          execution.indication.icd10Codes.otherConditions.map((condition, i) => {
                            const code = typeof condition === 'string' ? condition : condition.code;
                            const name = typeof condition === 'string' ? undefined : condition.name;
                            return (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                                {code}
                                {name && <span className="text-gray-500 font-normal">({name})</span>}
                              </span>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Suggested Matching Criteria Adjustments - Andy Pro style */}
                  {execution.indication.patientExpectations?.concerns && execution.indication.patientExpectations.concerns.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Suggested Matching Criteria Adjustments</span>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {execution.indication.patientExpectations.concerns.slice(0, 5).map((adjustment, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 text-xs bg-primary-50 text-primary-700 border border-primary-200 rounded"
                          >
                            + {adjustment}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Patient Expectations Tab - Matching Andy Pro layout */}
              {activeAnalysisTab === 'expectations' && (
                <div className="p-4 rounded-lg border border-gray-200 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {/* Icon color based on expectation level - Andy Pro style */}
                      <div className={clsx(
                        'p-1.5 rounded-md',
                        execution.indication.suitability === 'UNSUITABLE' ? 'bg-amber-100' : 'bg-green-100'
                      )}>
                        <Users className={clsx(
                          'h-4 w-4',
                          execution.indication.suitability === 'UNSUITABLE' ? 'text-amber-600' : 'text-green-600'
                        )} />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Patient Expectations</span>
                    </div>
                    {/* Expectation alignment badge - Andy Pro style */}
                    <span className={clsx(
                      'px-2 py-0.5 text-xs font-medium rounded capitalize',
                      execution.indication.suitability === 'UNSUITABLE' && 'bg-red-100 text-red-700',
                      execution.indication.suitability === 'REQUIRES_REVIEW' && 'bg-yellow-100 text-yellow-700',
                      execution.indication.suitability === 'SUITABLE' && 'bg-green-100 text-green-700'
                    )}>
                      {execution.indication.suitability === 'UNSUITABLE' ? 'Unrealistic' :
                       execution.indication.suitability === 'REQUIRES_REVIEW' ? 'Unclear' : 'Realistic'}
                    </span>
                  </div>

                  {execution.indication.patientExpectations ? (
                    <>
                      {/* Communication Needs - Andy Pro style */}
                      {execution.indication.patientExpectations.summary && (
                        <div className="mb-4">
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Communication Needs</span>
                          <p className="text-xs text-gray-600 leading-relaxed mt-1">
                            {execution.indication.patientExpectations.summary}
                          </p>
                        </div>
                      )}

                      {/* Ideal Provider Traits - Andy Pro style with icons */}
                      {execution.indication.patientExpectations.concerns && execution.indication.patientExpectations.concerns.length > 0 && (
                        <div>
                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                            Ideal Provider Traits ({execution.indication.patientExpectations.concerns.length})
                          </span>
                          <div className="space-y-2 mt-2">
                            {execution.indication.patientExpectations.concerns.map((trait, i) => {
                              // Map traits to relevant icons - same as Andy Pro
                              const icons = [Pill, Hospital, Briefcase, Timer, Users, Award];
                              const IconComponent = icons[i % icons.length];
                              return (
                                <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                                    {i + 1}
                                  </div>
                                  <IconComponent className="h-4 w-4 text-gray-400" />
                                  <span className="text-xs text-gray-700">{trait}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No patient expectations data available</p>
                  )}
                </div>
              )}

              {/* Documents Tab */}
              {activeAnalysisTab === 'documents' && (
                <div className={clsx(
                  "p-4 rounded-lg border bg-white",
                  execution.indication.documentFindings?.hasInconsistencies
                    ? "border-red-200"
                    : "border-gray-200"
                )}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={clsx(
                        "p-1.5 rounded-md",
                        execution.indication.documentFindings?.hasInconsistencies
                          ? "bg-red-100"
                          : "bg-purple-100"
                      )}>
                        <FileText className={clsx(
                          "h-4 w-4",
                          execution.indication.documentFindings?.hasInconsistencies
                            ? "text-red-600"
                            : "text-purple-600"
                        )} />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Document Findings</span>
                    </div>
                    {(execution.indication.documentFindings?.findings?.length ?? 0) > 0 && (
                      <span className={clsx(
                        "px-2 py-0.5 text-xs font-medium rounded",
                        execution.indication.documentFindings?.hasInconsistencies
                          ? "bg-red-100 text-red-700"
                          : "bg-purple-100 text-purple-700"
                      )}>
                        {execution.indication.documentFindings?.findings.length ?? 0} documents
                      </span>
                    )}
                  </div>

                  {execution.indication.documentFindings ? (
                    <div className="space-y-4">
                      {/* Verification Status */}
                      {execution.indication.documentFindings.verificationStatus && (
                        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                          <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                            Verification Status
                          </h4>
                          <p className="text-sm text-blue-700">
                            {execution.indication.documentFindings.verificationStatus}
                          </p>
                        </div>
                      )}

                      {/* Inconsistencies Warning */}
                      {execution.indication.documentFindings.hasInconsistencies &&
                       execution.indication.documentFindings.inconsistencies &&
                       execution.indication.documentFindings.inconsistencies.length > 0 && (
                        <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                          <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Inconsistencies Detected ({execution.indication.documentFindings.inconsistencies.length})
                          </h4>
                          <div className="space-y-3">
                            {execution.indication.documentFindings.inconsistencies.map((inconsistency, idx) => (
                              <div key={idx} className="p-3 rounded-lg bg-white border border-red-200">
                                <div className="flex items-start gap-3">
                                  <span className={clsx(
                                    'px-2 py-0.5 rounded text-xs font-medium capitalize shrink-0',
                                    inconsistency.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                    inconsistency.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                    inconsistency.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                                    'bg-gray-100 text-gray-600'
                                  )}>
                                    {inconsistency.severity}
                                  </span>
                                  <div className="flex-1">
                                    <p className="text-sm text-gray-700">{inconsistency.description}</p>
                                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                      <span>
                                        <strong>Document:</strong> {inconsistency.documentSource}
                                      </span>
                                      {inconsistency.questionnaireSource && (
                                        <span>
                                          <strong>Question:</strong> {inconsistency.questionnaireSource}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Document Findings */}
                      {execution.indication.documentFindings.findings &&
                       execution.indication.documentFindings.findings.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                            Document Analysis ({execution.indication.documentFindings.findings.length})
                          </h4>
                          <div className="space-y-3">
                            {execution.indication.documentFindings.findings.map((finding, idx) => (
                              <div key={idx} className="p-4 rounded-lg border border-gray-200 bg-white">
                                <div className="flex items-start gap-3">
                                  <FileText className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
                                  <div className="flex-1">
                                    <h5 className="font-medium text-gray-900 mb-2">{finding.documentName}</h5>

                                    {finding.observations && finding.observations.length > 0 && (
                                      <div className="mb-3">
                                        <p className="text-xs font-medium text-gray-500 mb-1">Observations:</p>
                                        <ul className="list-disc list-inside space-y-1">
                                          {finding.observations.map((obs, obsIdx) => (
                                            <li key={obsIdx} className="text-sm text-gray-700">{obs}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {finding.clinicalSignificance && (
                                      <div className="p-2 rounded bg-amber-50 border border-amber-200">
                                        <p className="text-xs font-medium text-amber-700 mb-1">Clinical Significance:</p>
                                        <p className="text-sm text-amber-900">{finding.clinicalSignificance}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No Findings */}
                      {(!execution.indication.documentFindings.findings || execution.indication.documentFindings.findings.length === 0) &&
                       (!execution.indication.documentFindings.inconsistencies || execution.indication.documentFindings.inconsistencies.length === 0) && (
                        <div className="p-4 rounded-lg bg-gray-50 text-center">
                          <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Documents were attached but no analysis findings are available</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-gray-50 text-center">
                      <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        {hasDocuments
                          ? "Documents were attached but no analysis findings are available"
                          : "No document findings available"}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Matching Criteria - Grouped by priority like Andy Pro with checkboxes */}
          <div className="border-b border-gray-100 p-6">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Matching Criteria</h4>
                <p className="text-xs text-gray-500 mt-0.5">Generated criteria for provider matching</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                {(() => {
                  // Count active criteria using the same key structure as the checkboxes
                  // Include manual criteria in total count
                  const totalCount = allCriteria.length;
                  let activeCount = 0;

                  // Iterate through grouped criteria to match key structure
                  priorityOrder.forEach((priority) => {
                    const items = groupedCriteria[priority];
                    if (items) {
                      items.forEach((_, idx) => {
                        const criteriaKey = `${execution.blindCode}-${priority}-${idx}`;
                        if (!disabledCriteria.has(criteriaKey)) {
                          activeCount++;
                        }
                      });
                    }
                  });

                  return `${activeCount}/${totalCount} criteria active`;
                })()}
              </div>
            </div>

            {execution.criteria.priorityExplanation && (
              <p className="mb-4 text-xs text-gray-600 italic bg-gray-50 p-2 rounded">
                {execution.criteria.priorityExplanation}
              </p>
            )}

            {/* Grid layout grouped by importance - matching Andy Pro workflow */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {priorityOrder.map((priority) => {
                const items = groupedCriteria[priority] || [];
                const config = priorityConfig[priority as keyof typeof priorityConfig];

                return (
                  <div key={priority} className={`rounded-lg border ${config.border} ${config.bg} p-3`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                          {config.label}
                        </span>
                        {items.length > 0 && (
                          <span className="text-xs text-gray-500">({items.length})</span>
                        )}
                      </div>
                      {!isReadOnly && (
                        <button
                          onClick={() => onOpenAddCriteriaModal(priority)}
                          className={`px-2 py-1 text-xs rounded-md font-medium flex items-center gap-1 ${config.addBtn}`}
                        >
                          <Plus className="w-3 h-3" />
                          Add
                        </button>
                      )}
                    </div>
                    {items.length === 0 ? (
                      <div className="text-center py-4 text-xs text-gray-500">
                        No {config.label.toLowerCase()} criteria yet
                      </div>
                    ) : (
                    <div className="space-y-2">
                      {items.map((c, idx) => {
                        // Build criterion key matching Andy Pro pattern
                        const criteriaKey = `${execution.blindCode}-${priority}-${idx}`;
                        const isDisabled = disabledCriteria.has(criteriaKey);
                        // Use name field first (from Andy Pro normalization), fallback to field
                        const displayName = c.name || c.field || '';
                        // Get rationale (detailed reasoning) - prefer dedicated field, fallback to reasoning
                        const rationale = c.rationale || c.reasoning || '';
                        // Only show description if it's a SHORT summary different from rationale
                        // If description equals rationale, skip it to avoid duplication
                        const displayDescription = c.description && c.description !== rationale
                          ? c.description
                          : '';
                        const isRationaleExpanded = expandedRationales.has(criteriaKey);
                        const isManual = (c as { _isManual?: boolean })._isManual === true;

                        return (
                          <div
                            key={c.id || idx}
                            className={clsx(
                              'rounded p-2 border transition-all',
                              isDisabled
                                ? 'bg-gray-50 border-gray-200 opacity-60'
                                : 'bg-white border-gray-100 hover:border-gray-200'
                            )}
                          >
                            <div className="flex items-start gap-2">
                              <label className={clsx('flex items-start gap-2 flex-1', !isReadOnly && 'cursor-pointer')}>
                                <input
                                  type="checkbox"
                                  checked={!isDisabled}
                                  onChange={() => !isReadOnly && onToggleCriterion(criteriaKey)}
                                  disabled={isReadOnly}
                                  className={clsx(
                                    'mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500',
                                    isReadOnly && 'cursor-not-allowed'
                                  )}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className={clsx(
                                      'text-sm font-medium',
                                      isDisabled ? 'text-gray-500 line-through' : 'text-gray-900'
                                    )}>
                                      {displayName}
                                    </p>
                                    {isManual && (
                                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded">
                                        Manual
                                      </span>
                                    )}
                                  </div>
                                  {/* Only show value if it's not already part of displayName */}
                                  {c.value && !displayName.includes(String(Array.isArray(c.value) ? c.value[0] : c.value)) && (
                                    <p className={clsx(
                                      'text-xs mt-0.5',
                                      isDisabled ? 'text-gray-400' : 'text-gray-600'
                                    )}>
                                      {Array.isArray(c.value) ? c.value.join(', ') : String(c.value)}
                                    </p>
                                  )}
                                  {displayDescription && (
                                    <p className={clsx(
                                      'text-xs mt-1',
                                      isDisabled ? 'text-gray-400' : 'text-gray-500'
                                    )}>
                                      {displayDescription}
                                    </p>
                                  )}
                                </div>
                              </label>
                              {/* Delete button for manual criteria */}
                              {isManual && !isReadOnly && (
                                <button
                                  onClick={() => onOpenDeleteCriteriaModal(c.id, displayName, priority)}
                                  className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Delete criteria"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            {/* Expandable Rationale Section */}
                            {rationale && (
                              <div className="mt-2 ml-6">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRationale(criteriaKey);
                                  }}
                                  className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
                                >
                                  {isRationaleExpanded ? (
                                    <ChevronUp className="h-3 w-3" />
                                  ) : (
                                    <ChevronDown className="h-3 w-3" />
                                  )}
                                  {isRationaleExpanded ? 'Hide rationale' : 'Show rationale'}
                                </button>
                                {isRationaleExpanded && (
                                  <div className="mt-2 p-2 bg-amber-50 border border-amber-100 rounded text-xs text-gray-700 leading-relaxed">
                                    {rationale}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );
              })}
            </div>

            {allCriteria.length === 0 && (
              <p className="text-sm text-gray-500 italic text-center py-4">No matching criteria generated</p>
            )}
          </div>

          {/* Evaluation Controls - hidden when no evaluation exists in read-only mode */}
          {!hideEvaluationControls && (
          <div className="bg-gray-50 p-6">
            {/* Verdict Selection - Full width */}
            <div>
              <label className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-700">
                <Star className="h-4 w-4" />
                Clinical Verdict
                {verdict && (
                  <span className="ml-auto text-xs text-green-600 font-normal flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    {VERDICTS.find((v) => v.value === verdict)?.label}
                  </span>
                )}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {VERDICTS.map((v) => {
                  const Icon = v.icon;
                  return (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => !isReadOnly && onVerdictChange(v.value)}
                      disabled={isReadOnly}
                      className={clsx(
                        'flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all',
                        verdict === v.value
                          ? v.color + ' ring-2 ring-offset-2'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300 bg-white',
                        isReadOnly && 'cursor-not-allowed opacity-75'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {v.label}
                      {!isReadOnly && (
                        <kbd className="ml-auto px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono text-gray-400">
                          {v.shortcut}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Per-execution Notes */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                <MessageSquare className="h-4 w-4" />
                Notes for this execution
              </label>
              <textarea
                value={note || ''}
                onChange={(e) => !isReadOnly && onNoteChange(e.target.value)}
                rows={2}
                placeholder={isReadOnly ? 'No notes recorded' : 'Add specific notes about this model\'s output...'}
                disabled={isReadOnly}
                className={clsx(
                  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
                  isReadOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
                )}
              />
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}

export function BlindEvaluation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [verdicts, setVerdicts] = useState<Record<string, string>>({});
  const [rankings, setRankings] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [executionNotes, setExecutionNotes] = useState<Record<string, string>>({});
  // Track disabled criteria per execution (matching Andy Pro workflow)
  const [disabledCriteria, setDisabledCriteria] = useState<Set<string>>(new Set());
  // Manual criteria management (matching Andy Pro workflow)
  const [manualCriteriaByExecution, setManualCriteriaByExecution] = useState<Record<string, Array<{
    id: string;
    name: string;
    description: string;
    priority: string;
  }>>>({});
  const [addCriteriaModal, setAddCriteriaModal] = useState<{
    blindCode: string;
    priority: string;
    name: string;
    description: string;
  } | null>(null);
  const [deleteCriteriaModal, setDeleteCriteriaModal] = useState<{
    blindCode: string;
    criteriaId: string;
    name: string;
    priority: string;
  } | null>(null);
  // Track focused execution for keyboard navigation
  const [focusedIndex, setFocusedIndex] = useState(0);
  // Track expanded executions (reserved for future use)
  const [, setExpandedCards] = useState<Set<number>>(new Set([0]));
  void setExpandedCards; // Suppress unused warning
  // Show keyboard shortcuts help
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Get current user for matching evaluation
  const user = useAuthStore((state) => state.user);
  const currentUserEmail = user?.username;

  // Fetch from blind-view endpoint which includes indication/criteria data
  const { data: blindViewResponse, isLoading, error } = useQuery({
    queryKey: ['validation', 'test-run', id, 'blind-view'],
    queryFn: () => api.get<{
      testRun: { id: string; name: string; status: string; isRevealed: boolean; runType?: RunType };
      testCase: TestRun['testCase'];
      executions: Execution[];
    }>(`/api/validation/test-runs/${id}/blind-view`),
    enabled: !!id,
  });

  // Fetch test run detail to get existing evaluations
  const { data: testRunDetailResponse } = useQuery({
    queryKey: ['validation', 'test-run', id, 'detail'],
    queryFn: () => api.get<{
      data: {
        id: string;
        evaluationSets: Array<{
          id: string;
          evaluator: { id: string; name: string; email: string };
          rankings: string[];
          selectedBest: string | null;
          evaluatorNotes: string | null;
          completedAt: string | null;
          // Criteria customization state
          disabledCriteria?: Record<string, string[]>;
          manualCriteria?: Record<string, Array<{ id: string; name: string; description: string; priority: string }>>;
          items: Array<{
            blindCode: string;
            verdict: string;
            verdictNotes?: string;
          }>;
        }>;
      };
    }>(`/api/validation/test-runs/${id}`),
    enabled: !!id && !!blindViewResponse?.testRun?.isRevealed,
  });

  // Find current user's existing evaluation
  const existingEvaluation = testRunDetailResponse?.data?.evaluationSets?.find(
    (evalSet) => evalSet.evaluator.email === currentUserEmail && evalSet.completedAt
  );

  // Reconstruct testRun structure from blind-view response
  const testRun = blindViewResponse ? {
    ...blindViewResponse.testRun,
    testCase: blindViewResponse.testCase,
    executions: blindViewResponse.executions,
  } as TestRun : undefined;

  // Check if we're in read-only mode (only if revealed AND has existing evaluation)
  const hasExistingEvaluation = !!existingEvaluation;
  const isReadOnly = (testRun?.isRevealed ?? false) && hasExistingEvaluation;

  const submitMutation = useMutation({
    mutationFn: (data: {
      verdicts: Record<string, string>;
      rankings: string[];
      notes?: string;
      executionNotes?: Record<string, string>;
      disabledCriteria?: Record<string, string[]>;
      manualCriteria?: Record<string, Array<{ id: string; name: string; description: string; priority: string }>>;
    }) => api.post(`/api/validation/test-runs/${id}/evaluate`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] });
      // Return to test run detail to continue workflow (Phase 2)
      // Results are NOT revealed here - reveal only happens at final Analysis step
      navigate(`/validation/test-runs/${id}`);
    },
  });

  // Pre-populate form with existing evaluation data
  useEffect(() => {
    if (existingEvaluation) {
      // Populate verdicts from existing items
      const verdictMap: Record<string, string> = {};
      const notesMap: Record<string, string> = {};
      existingEvaluation.items.forEach((item) => {
        verdictMap[item.blindCode] = item.verdict;
        if (item.verdictNotes) {
          notesMap[item.blindCode] = item.verdictNotes;
        }
      });
      setVerdicts(verdictMap);
      setExecutionNotes(notesMap);

      // Populate rankings
      if (existingEvaluation.rankings && existingEvaluation.rankings.length > 0) {
        const rankingsMap: Record<string, number> = {};
        existingEvaluation.rankings.forEach((code, index) => {
          rankingsMap[code] = index + 1;
        });
        setRankings(rankingsMap);
      }

      // Populate notes
      if (existingEvaluation.evaluatorNotes) {
        setNotes(existingEvaluation.evaluatorNotes);
      }

      // Restore disabled criteria state
      if (existingEvaluation.disabledCriteria && Object.keys(existingEvaluation.disabledCriteria).length > 0) {
        // Convert from { blindCode: ["priority-index", ...] } to Set of "blindCode-priority-index"
        const restoredDisabledCriteria = new Set<string>();
        Object.entries(existingEvaluation.disabledCriteria).forEach(([blindCode, keys]) => {
          keys.forEach((key) => {
            restoredDisabledCriteria.add(`${blindCode}-${key}`);
          });
        });
        setDisabledCriteria(restoredDisabledCriteria);
        console.log('[BlindEvaluation] Restored disabled criteria:', restoredDisabledCriteria.size, 'items');
      }

      // Restore manual criteria
      if (existingEvaluation.manualCriteria && Object.keys(existingEvaluation.manualCriteria).length > 0) {
        setManualCriteriaByExecution(existingEvaluation.manualCriteria);
        console.log('[BlindEvaluation] Restored manual criteria for', Object.keys(existingEvaluation.manualCriteria).length, 'executions');
      }
    }
  }, [existingEvaluation]);

  const handleVerdictChange = (blindCode: string, verdict: string) => {
    if (isReadOnly) return;
    setVerdicts((prev) => ({ ...prev, [blindCode]: verdict }));
  };

  // Handle rankings change from AutoRankingSection
  const handleRankingsChange = (newRankings: Record<string, number>) => {
    setRankings(newRankings);
  };

  const handleExecutionNoteChange = (blindCode: string, note: string) => {
    setExecutionNotes((prev) => ({ ...prev, [blindCode]: note }));
  };

  // Toggle criterion enabled/disabled (matching Andy Pro workflow)
  const handleToggleCriterion = (criteriaKey: string) => {
    setDisabledCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(criteriaKey)) {
        next.delete(criteriaKey);
      } else {
        next.add(criteriaKey);
      }
      return next;
    });
  };

  // Add manual criteria (matching Andy Pro workflow)
  const handleAddCriteria = (blindCode: string, name: string, description: string, priority: string) => {
    const criteriaId = `manual-${Date.now()}`;
    setManualCriteriaByExecution((prev) => ({
      ...prev,
      [blindCode]: [
        ...(prev[blindCode] || []),
        { id: criteriaId, name, description, priority },
      ],
    }));
    setAddCriteriaModal(null);
  };

  // Delete manual criteria
  const handleDeleteCriteria = (blindCode: string, criteriaId: string) => {
    setManualCriteriaByExecution((prev) => ({
      ...prev,
      [blindCode]: (prev[blindCode] || []).filter((c) => c.id !== criteriaId),
    }));
    setDeleteCriteriaModal(null);
  };

  // Toggle card expansion
  const toggleCardExpansion = useCallback((index: number) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Keyboard shortcuts handler
  useEffect(() => {
    const completedExecutions = testRun?.executions.filter((e) => e.status === 'COMPLETED') || [];
    const currentExecution = completedExecutions[focusedIndex];

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        return;
      }

      // Verdict shortcuts (1-6)
      if (currentExecution && e.key >= '1' && e.key <= '6') {
        const verdictIndex = parseInt(e.key) - 1;
        if (verdictIndex < VERDICTS.length) {
          handleVerdictChange(currentExecution.blindCode, VERDICTS[verdictIndex].value);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, completedExecutions.length - 1));
          break;
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'e':
        case 'Enter':
          if (e.key === 'Enter' && Object.keys(verdicts).length === completedExecutions.length) {
            // Submit when Enter is pressed and all verdicts are complete
            handleSubmit();
          } else if (e.key === 'e') {
            // Toggle expansion
            toggleCardExpansion(focusedIndex);
          }
          break;
        case '?':
          setShowShortcuts(true);
          break;
        case 'Escape':
          setShowShortcuts(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedIndex, testRun, verdicts, toggleCardExpansion]);

  const handleSubmit = () => {
    const completedExecutions = testRun?.executions.filter((e) => e.status === 'COMPLETED') || [];

    // Validate all executions have verdicts
    const allVerdictsSet = completedExecutions.every((e) => verdicts[e.blindCode]);
    if (!allVerdictsSet) {
      alert('Please provide a verdict for all executions');
      return;
    }

    // Auto-generate rankings from verdicts if not already set
    let finalRankings = rankings;
    if (Object.keys(rankings).length !== completedExecutions.length) {
      // Generate rankings from verdict scores
      const rankedByScore = completedExecutions
        .map((e) => ({
          blindCode: e.blindCode,
          score: getVerdictScore(verdicts[e.blindCode]),
        }))
        .sort((a, b) => b.score - a.score);

      finalRankings = {};
      rankedByScore.forEach((item, idx) => {
        finalRankings[item.blindCode] = idx + 1;
      });
    }

    // Convert rankings map to ordered array
    const orderedRankings = completedExecutions
      .sort((a, b) => (finalRankings[a.blindCode] || 999) - (finalRankings[b.blindCode] || 999))
      .map((e) => e.blindCode);

    // Filter out empty execution notes
    const filteredExecutionNotes = Object.fromEntries(
      Object.entries(executionNotes).filter(([, note]) => note.trim())
    );

    // Convert disabled criteria from flat Set to per-execution record
    // disabledCriteria Set contains keys like "A1-critical-0", need to group by blind code
    const disabledCriteriaByExecution: Record<string, string[]> = {};
    disabledCriteria.forEach((key) => {
      // Key format: "blindCode-priority-index"
      const parts = key.split('-');
      if (parts.length >= 3) {
        const blindCode = parts[0];
        const criteriaKey = parts.slice(1).join('-'); // "priority-index"
        if (!disabledCriteriaByExecution[blindCode]) {
          disabledCriteriaByExecution[blindCode] = [];
        }
        disabledCriteriaByExecution[blindCode].push(criteriaKey);
      }
    });

    submitMutation.mutate({
      verdicts,
      rankings: orderedRankings,
      notes: notes.trim() || undefined,
      executionNotes: Object.keys(filteredExecutionNotes).length > 0 ? filteredExecutionNotes : undefined,
      disabledCriteria: Object.keys(disabledCriteriaByExecution).length > 0 ? disabledCriteriaByExecution : undefined,
      manualCriteria: Object.keys(manualCriteriaByExecution).length > 0 ? manualCriteriaByExecution : undefined,
    });
  };

  if (isLoading) {
    return (
      <LoadingState
        message="Loading evaluation data..."
        showLongLoadingMessage={true}
        longLoadingThreshold={5000}
        longLoadingMessage="Loading detailed analysis for each model. This may take a moment."
      />
    );
  }

  if (error || !testRun) {
    return (
      <ErrorState
        title="Test Run Not Found"
        message={error instanceof Error ? error.message : "The test run you're looking for doesn't exist."}
        helpText="Check the URL or go back to the test runs list"
        onRetry={() => queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] })}
        variant="card"
      />
    );
  }

  // If revealed, continue to render in read-only mode (whether or not evaluation exists)

  const completedExecutions = (testRun.executions || []).filter((e) => e.status === 'COMPLETED');

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Model Validation', href: '/validation', icon: Home },
    { label: 'Test Runs', href: '/validation/test-runs', icon: Play },
    { label: testRun.name, href: `/validation/test-runs/${id}` },
    { label: 'Blind Evaluation', icon: ClipboardCheck },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {/* Add Criteria Modal */}
      {addCriteriaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setAddCriteriaModal(null)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className={clsx(
              'px-5 py-4 border-b',
              addCriteriaModal.priority === 'critical' && 'bg-red-50 border-red-200',
              addCriteriaModal.priority === 'high' && 'bg-orange-50 border-orange-200',
              addCriteriaModal.priority === 'medium' && 'bg-yellow-50 border-yellow-200',
              addCriteriaModal.priority === 'low' && 'bg-gray-50 border-gray-200',
            )}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className={clsx(
                    'h-5 w-5',
                    addCriteriaModal.priority === 'critical' && 'text-red-600',
                    addCriteriaModal.priority === 'high' && 'text-orange-600',
                    addCriteriaModal.priority === 'medium' && 'text-yellow-600',
                    addCriteriaModal.priority === 'low' && 'text-gray-600',
                  )} />
                  <h3 className="text-lg font-semibold text-gray-900">Add Criteria</h3>
                </div>
                <button
                  onClick={() => setAddCriteriaModal(null)}
                  className="p-1.5 rounded-lg hover:bg-white/50 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Adding to <span className={clsx(
                  'font-medium',
                  addCriteriaModal.priority === 'critical' && 'text-red-700',
                  addCriteriaModal.priority === 'high' && 'text-orange-700',
                  addCriteriaModal.priority === 'medium' && 'text-yellow-700',
                  addCriteriaModal.priority === 'low' && 'text-gray-700',
                )}>
                  {addCriteriaModal.priority === 'critical' ? 'Critical' :
                   addCriteriaModal.priority === 'high' ? 'High Priority' :
                   addCriteriaModal.priority === 'medium' ? 'Medium Priority' : 'Low Priority'}
                </span> criteria for {addCriteriaModal.blindCode}
              </p>
            </div>
            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!addCriteriaModal.name.trim()) return;
                handleAddCriteria(
                  addCriteriaModal.blindCode,
                  addCriteriaModal.name.trim(),
                  addCriteriaModal.description.trim(),
                  addCriteriaModal.priority
                );
              }}
              className="p-5 space-y-4"
            >
              <div>
                <label htmlFor="criteria-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Criteria Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="criteria-name"
                  type="text"
                  value={addCriteriaModal.name}
                  onChange={(e) => setAddCriteriaModal(prev => prev ? { ...prev, name: e.target.value } : null)}
                  placeholder="e.g., Experience with rare conditions"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="criteria-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="criteria-description"
                  value={addCriteriaModal.description}
                  onChange={(e) => setAddCriteriaModal(prev => prev ? { ...prev, description: e.target.value } : null)}
                  placeholder="Additional details about this criteria..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm resize-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setAddCriteriaModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!addCriteriaModal.name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Criteria
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Criteria Confirmation Modal */}
      {deleteCriteriaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeleteCriteriaModal(null)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 bg-red-50 border-b border-red-200">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Criteria</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
            </div>
            {/* Content */}
            <div className="p-5">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete the criteria:
              </p>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-900">{deleteCriteriaModal.name}</p>
                <p className="text-xs text-gray-500 mt-1 capitalize">
                  {deleteCriteriaModal.priority === 'critical' ? 'Critical' :
                   deleteCriteriaModal.priority === 'high' ? 'High Priority' :
                   deleteCriteriaModal.priority === 'medium' ? 'Medium Priority' : 'Low Priority'} criteria
                </p>
              </div>
            </div>
            {/* Actions */}
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setDeleteCriteriaModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteCriteria(deleteCriteriaModal.blindCode, deleteCriteriaModal.criteriaId);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete Criteria
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(`/validation/test-runs/${id}`)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Blind Evaluation</h1>
            <p className="text-sm text-gray-500">{testRun.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isReadOnly && (
            <button
              onClick={() => setShowShortcuts(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              title="Keyboard shortcuts"
            >
              <Keyboard className="h-4 w-4" />
              <span className="hidden sm:inline">Shortcuts</span>
              <kbd className="hidden sm:inline ml-1 px-1.5 py-0.5 bg-gray-100 rounded text-xs">?</kbd>
            </button>
          )}
          {isReadOnly ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200">
              <Eye className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">Revealed (Read Only)</span>
            </div>
          ) : testRun?.isRevealed ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
              <Eye className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">Revealed Mode</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
              <EyeOff className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">Double-Blind Mode</span>
            </div>
          )}
        </div>
      </div>

      {/* Progress Indicator */}
      <ProgressIndicator
        current={focusedIndex}
        total={completedExecutions.length}
        verdicts={verdicts}
        rankings={rankings}
      />

      {/* Notice when evaluating after reveal (not blind) */}
      {testRun?.isRevealed && !hasExistingEvaluation && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Results have been revealed</p>
              <p className="text-xs text-amber-600 mt-0.5">
                You can still submit an evaluation, but model identities are now visible.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Context Panel */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Test Case Context</h2>
          </div>
          <Link
            to={`/validation/test-cases/${testRun.testCase.id}`}
            className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700 font-medium"
          >
            View Test Case
            <ExternalLink className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <span className="text-xs text-gray-500">Test Case</span>
            <p className="font-medium text-gray-900">{testRun.testCase.name}</p>
            <p className="text-sm text-gray-500">{testRun.testCase.code}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Condition</span>
            <p className="text-sm text-gray-900">{testRun.testCase.condition}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Patient</span>
            <p className="text-sm text-gray-900">
              {testRun.testCase.patientInfo.age} y/o {testRun.testCase.patientInfo.gender}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Chief Complaint</span>
            <p className="text-sm text-gray-900">{testRun.testCase.patientInfo.chiefComplaint}</p>
          </div>
        </div>
      </div>

      {/* Execution Cards */}
      <div className="space-y-6">
        {completedExecutions.map((execution, index) => (
          <div
            key={execution.id}
            className={clsx(
              'rounded-xl transition-all',
              focusedIndex === index && 'ring-2 ring-amber-400 ring-offset-2'
            )}
            onClick={() => setFocusedIndex(index)}
          >
            <ExecutionCard
              execution={execution}
              verdict={verdicts[execution.blindCode]}
              note={executionNotes[execution.blindCode]}
              onVerdictChange={(v) => handleVerdictChange(execution.blindCode, v)}
              onNoteChange={(n) => handleExecutionNoteChange(execution.blindCode, n)}
              hasDocuments={(testRun.testCase.documents?.length ?? 0) > 0}
              disabledCriteria={disabledCriteria}
              onToggleCriterion={handleToggleCriterion}
              manualCriteria={manualCriteriaByExecution[execution.blindCode] || []}
              onOpenAddCriteriaModal={(priority) => setAddCriteriaModal({
                blindCode: execution.blindCode,
                priority,
                name: '',
                description: '',
              })}
              onOpenDeleteCriteriaModal={(criteriaId, name, priority) => setDeleteCriteriaModal({
                blindCode: execution.blindCode,
                criteriaId,
                name,
                priority,
              })}
              isReadOnly={isReadOnly}
              hideEvaluationControls={false}
            />
          </div>
        ))}
      </div>

      {/* Auto-Ranking Section */}
      <AutoRankingSection
        executions={completedExecutions}
        verdicts={verdicts}
        rankings={rankings}
        onRankingsChange={handleRankingsChange}
        isReadOnly={isReadOnly}
      />

      {/* Notes */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
          <MessageSquare className="h-4 w-4" />
          Evaluation Notes {!isReadOnly && '(Optional)'}
        </label>
        <textarea
          value={notes}
          onChange={(e) => !isReadOnly && setNotes(e.target.value)}
          rows={3}
          placeholder={isReadOnly ? 'No notes recorded' : 'Add any notes about your evaluation...'}
          disabled={isReadOnly}
          className={clsx(
            'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
            isReadOnly && 'bg-gray-50 cursor-not-allowed'
          )}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-6">
        <div className="text-sm text-gray-600">
          {Object.keys(verdicts).length === completedExecutions.length ? (
            <span className="flex items-center gap-2 text-green-600">
              <Check className="h-4 w-4" />
              {isReadOnly ? `All ${completedExecutions.length} verdicts recorded` : `All ${completedExecutions.length} verdicts complete • Rankings ready`}
            </span>
          ) : (
            <span>
              {Object.keys(verdicts).length} of {completedExecutions.length} verdicts {isReadOnly ? 'recorded' : 'complete'}
            </span>
          )}
        </div>
        {!isReadOnly && (
          <button
            onClick={handleSubmit}
            disabled={submitMutation.isPending || Object.keys(verdicts).length !== completedExecutions.length}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Submit Evaluation
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
        {isReadOnly && (
          <Link
            to={`/validation/test-runs/${id}/results`}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            <BarChart3 className="h-4 w-4" />
            View Results
          </Link>
        )}
      </div>

      {/* Error Display */}
      {submitMutation.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Failed to submit evaluation</span>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {submitMutation.error instanceof Error
              ? submitMutation.error.message
              : 'An unexpected error occurred'}
          </p>
        </div>
      )}
    </div>
  );
}
